import React, { createContext, useContext, useState, useCallback, ReactNode, useRef, useEffect, useId } from 'react';
import { createPortal } from 'react-dom';
import { useDialog } from '@react-aria/dialog';
import { useOverlay, useModal, usePreventScroll } from '@react-aria/overlays';
import { FocusScope } from '@react-aria/focus';
import {IconButton} from "./IconButton";
import {CloseIcon} from "./Icons";

// ============================================================================
// Types
// ============================================================================

export type BladeWidth = 'small' | 'medium' | 'large' | number;

export interface BladeConfig {
    id: string;
    content: ReactNode;
    width?: BladeWidth;
    isDismissible?: boolean;
    onClose?: () => void;
    preventBackdropClose?: boolean;
}

export interface BladeInstance extends BladeConfig {
    depth: number;
    isTopmost: boolean;
    totalBlades: number;
}

export interface BladeContextValue {
    blades: BladeInstance[];
    registerBlade: (config: BladeConfig) => void;
    unregisterBlade: (id: string) => void;
    closeBlade: (id: string) => void;
    closeAllBlades: () => void;
    getBladeDepth: (id: string) => number;
}

export interface BladeProps {
    id?: string;
    isOpen: boolean;
    onClose: () => void;
    width?: BladeWidth;
    preventBackdropClose?: boolean;
    children: ReactNode;
    className?: string;
    isDismissible?: boolean;
    hideCloseButton?: boolean;
}

export interface BladeHeaderProps {
    title: string;
    titleProps?: any;
    onClose?: () => void;
    children?: ReactNode;
    className?: string;
    hideCloseButton?: boolean;
}

export interface BladeBodyProps {
    children: ReactNode;
    className?: string;
}

export interface BladeFooterProps {
    children: ReactNode;
    align?: 'left' | 'right' | 'between';
    className?: string;
}

// ============================================================================
// Internal Components
// ============================================================================

// Blade Header Component
export const BladeHeader: React.FC<BladeHeaderProps> = ({
                                                            title,
                                                            titleProps = {},
                                                            onClose,
                                                            children,
                                                            className = '',
                                                            hideCloseButton = false,
                                                        }) => {
    return (
        <header className={`blade-header ${className}`}>
            {children || (
                <>
                    <h2 {...titleProps} className="blade-title">
                        {title}
                    </h2>

                    {!hideCloseButton && (
                        <IconButton
                            aria-label="Close"
                            variant="ghost"
                            onPress={onClose}>
                            <CloseIcon />
                        </IconButton>
                    )}
                </>
            )}
        </header>
    );
};

// Blade Body Component
export const BladeBody: React.FC<BladeBodyProps> = ({
                                                        children,
                                                        className = '',
                                                    }) => {
    return (
        <div className={`blade-body ${className}`}>
            {children}
        </div>
    );
};

// Blade Footer Component
export const BladeFooter: React.FC<BladeFooterProps> = ({
                                                            children,
                                                            align = 'right',
                                                            className = '',
                                                        }) => {
    const justifyContent = {
        left: 'flex-start',
        right: 'flex-end',
        between: 'space-between',
    }[align];

    return (
        <div
            className={`blade-footer ${className}`}
            style={{ justifyContent }}
        >
            {children}
        </div>
    );
};

// ============================================================================
// Blade Renderer
// ============================================================================

interface BladeRendererProps {
    blade: BladeInstance;
}

const getBladeWidth = (width: BladeWidth): string => {
    if (typeof width === 'number') return `${width}px`;

    const widthMap = {
        small: '400px',
        medium: '600px',
        large: '800px',
    };

    return widthMap[width];
};

const BladeRenderer: React.FC<BladeRendererProps> = ({ blade }) => {
    const overlayRef = useRef<HTMLDivElement>(null);
    const contentRef = useRef<HTMLDivElement>(null);

    const {
        id,
        content,
        width = 'medium',
        isDismissible = true,
        onClose,
        preventBackdropClose = false,
        depth,
        isTopmost,
        totalBlades,
    } = blade;

    const handleClose = () => {
        onClose?.();
    };

    const { overlayProps, underlayProps } = useOverlay(
        {
            isOpen: true,
            onClose: handleClose,
            isDismissable: isDismissible && !preventBackdropClose,
            shouldCloseOnInteractOutside: () => isTopmost && isDismissible && !preventBackdropClose,
        },
        overlayRef
    );

    const { modalProps } = useModal({
        isDisabled: !isTopmost,
    });

    const { dialogProps } = useDialog(
        {
            role: 'dialog',
        },
        overlayRef
    );

    // Prevent scroll only for topmost blade
    usePreventScroll({
        isDisabled: !isTopmost,
    });

    // Calculate blade positioning
    const bladeWidthPx = getBladeWidth(width);
    const previousBladesWidth = depth * 60; // Each previous blade shows 60px
    const translateX = previousBladesWidth;

    // Handle ESC key for topmost blade
    useEffect(() => {
        if (!isTopmost || !isDismissible) return;

        const handleKeyDown = (e: KeyboardEvent) => {
            if (e.key === 'Escape') {
                e.preventDefault();
                handleClose();
            }
        };

        document.addEventListener('keydown', handleKeyDown);
        return () => document.removeEventListener('keydown', handleKeyDown);
    }, [isTopmost, isDismissible]);

    const bladeClassName = [
        'blade',
        isTopmost ? 'blade--topmost' : 'blade--background',
        `blade--depth-${depth}`,
    ].join(' ');

    return (
        <div role="presentation" className="fixed inset-0 z-[1300] flex items-center justify-center">
            {/* Backdrop - only render for first blade (depth 0) */}
            {depth === 0 && (
                <div
                    {...underlayProps}
                    className="fixed inset-0 bg-black/50 backdrop-blur-sm animate-fade-in z-[999]"
                    onClick={isDismissible && !preventBackdropClose ? handleClose : undefined}
                    style={{
                        opacity: 1,
                        transition: 'opacity 225ms cubic-bezier(0.4, 0, 0.2, 1)',
                    }}
                />
            )}

            <FocusScope contain={isTopmost} restoreFocus autoFocus>
                <div
                    {...overlayProps}
                    {...dialogProps}
                    {...modalProps}
                    ref={overlayRef}
                    className={bladeClassName}
                    style={{
                        width: bladeWidthPx,
                        transform: `translateX(-${translateX}px)`,
                        zIndex: 1000 + depth,
                        pointerEvents: isTopmost ? 'auto' : 'none',
                    }}
                >
                    <div className="blade-content-wrapper" ref={contentRef}>
                        {content}
                    </div>
                </div>
            </FocusScope>
        </div>
    );
};

// ============================================================================
// Blade Container
// ============================================================================

const BladeContainer: React.FC = () => {
    const { blades } = useBlade();
    const containerRef = useRef<HTMLDivElement | null>(null);

    useEffect(() => {
        if (!containerRef.current) {
            const container = document.createElement('div');
            container.id = 'blade-container';
            container.setAttribute('data-blade-container', 'true');
            document.body.appendChild(container);
            containerRef.current = container;
        }

        return () => {
            if (containerRef.current && blades.length === 0) {
                containerRef.current.remove();
                containerRef.current = null;
            }
        };
    }, [blades.length]);

    // Set aria-hidden on root when blades are open
    useEffect(() => {
        if (blades.length > 0) {
            const root = document.getElementById('root');
            if (root) {
                root.setAttribute('aria-hidden', 'true');
                return () => {
                    root.removeAttribute('aria-hidden');
                };
            }
        }
    }, [blades.length]);

    if (!containerRef.current || blades.length === 0) {
        return null;
    }

    return createPortal(
        <>
            {/* Render all blades */}
            {blades.map((blade) => (
                <BladeRenderer key={blade.id} blade={blade} />
            ))}
        </>,
        containerRef.current
    );
};

// ============================================================================
// Blade Context & Provider
// ============================================================================

const BladeContext = createContext<BladeContextValue | null>(null);

export const BladeProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
    const [blades, setBlades] = useState<BladeInstance[]>([]);

    const updateBladeDepths = useCallback((bladeList: BladeConfig[]): BladeInstance[] => {
        return bladeList.map((blade, index) => ({
            ...blade,
            depth: index,
            isTopmost: index === bladeList.length - 1,
            totalBlades: bladeList.length,
        }));
    }, []);

    const registerBlade = useCallback((config: BladeConfig) => {
        setBlades(prev => {
            // Check if blade already exists
            const existingIndex = prev.findIndex(b => b.id === config.id);

            if (existingIndex !== -1) {
                // Update existing blade
                const updated = [...prev];
                updated[existingIndex] = {
                    ...updated[existingIndex],
                    ...config,
                    depth: existingIndex,
                    isTopmost: existingIndex === prev.length - 1,
                    totalBlades: prev.length,
                };
                return updated;
            }

            // Add new blade
            const newBlades = [...prev, config as BladeInstance];
            return updateBladeDepths(newBlades);
        });
    }, [updateBladeDepths]);

    const unregisterBlade = useCallback((id: string) => {
        setBlades(prev => {
            const index = prev.findIndex(b => b.id === id);
            if (index === -1) return prev;

            // Remove this blade and all blades after it
            const remainingBlades = prev.slice(0, index);

            // Call onClose for closed blades
            prev.slice(index).forEach(blade => {
                blade.onClose?.();
            });

            return updateBladeDepths(remainingBlades);
        });
    }, [updateBladeDepths]);

    const closeBlade = useCallback((id: string) => {
        unregisterBlade(id);
    }, [unregisterBlade]);

    const closeAllBlades = useCallback(() => {
        blades.forEach(blade => blade.onClose?.());
        setBlades([]);
    }, [blades]);

    const getBladeDepth = useCallback((id: string): number => {
        return blades.findIndex(b => b.id === id);
    }, [blades]);

    const value: BladeContextValue = {
        blades,
        registerBlade,
        unregisterBlade,
        closeBlade,
        closeAllBlades,
        getBladeDepth,
    };

    return (
        <BladeContext.Provider value={value}>
            {children}
            <BladeContainer />
        </BladeContext.Provider>
    );
};

export const useBlade = (): BladeContextValue => {
    const context = useContext(BladeContext);
    if (!context) {
        throw new Error('useBlade must be used within BladeProvider');
    }
    return context;
};

// ============================================================================
// Main Blade Component
// ============================================================================

/**
 * Blade component with declarative API
 *
 * @example
 * <Blade isOpen={isOpen} onClose={handleClose} width="medium">
 *   <BladeHeader title="My Blade" onClose={handleClose} />
 *   <BladeBody>Content here</BladeBody>
 *   <BladeFooter>Footer content</BladeFooter>
 * </Blade>
 */
export const Blade: React.FC<BladeProps> = ({
                                                id,
                                                isOpen,
                                                onClose,
                                                width = 'medium',
                                                isDismissible = true,
                                                preventBackdropClose = false,
                                                children,
                                                className = '',
                                            }) => {
    const generatedId = useId();
    const bladeId = id || `blade-${generatedId}`;
    const { registerBlade, unregisterBlade } = useBlade();

    useEffect(() => {
        if (isOpen) {
            registerBlade({
                id: bladeId,
                content: (
                    <div className={`blade-inner ${className}`}>
                        {children}
                    </div>
                ),
                width,
                isDismissible,
                onClose,
                preventBackdropClose,
            });
        } else {
            unregisterBlade(bladeId);
        }

        return () => {
            if (isOpen) {
                unregisterBlade(bladeId);
            }
        };
    }, [isOpen, bladeId, width, isDismissible, preventBackdropClose]);

    // Update content when children change
    useEffect(() => {
        if (isOpen) {
            registerBlade({
                id: bladeId,
                content: (
                    <div className={`blade-inner ${className}`}>
                        {children}
                    </div>
                ),
                width,
                isDismissible,
                onClose,
                preventBackdropClose,
            });
        }
    }, [children, className, isOpen]);

    return null; // Blade renders via portal
};